// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0

import { getApiUrl, isKeycloakEnabled } from '../../config.js';
import { post, upload } from '../../utils/CoreService';

function mapItems(items, type, timestamp) {
  return items.map((item) => ({
    ...item,
    type,
    timestamp,
    rating: 0,
  }));
}

export async function fetchRAGResponseDatabase({
  timestamp,
  sources,
  currentQuestion,
  llm,
  updateRequestState,
  resetTextAreaHeight,
  uiTexts,
}) {
  const dbSources = sources.filter((s) => s.type === 'database' || s.type === 'database_all_sources');
  if (dbSources.length === 0) return;

  const formatDateRange = (source) => ({
    start_date: source.meta.date_range[0],
    end_date: source.meta.date_range[1],
  });

  const meta_data_filters = dbSources
    .filter((item) => item.type === 'database' || item.type === 'database_all_sources')
    .map((s) => ({
      source: s.type === 'database' ? s.label : null,
      // only add date range filter if date_filter is set to `true`
      ...(s.date_filter ? formatDateRange(s) : {}),
    }));

  const body = {
    question: currentQuestion,
    meta_data_filters,
    language_model: llm.name,
    max_chunks_to_use: llm.max_chunks_to_use,
    request_timestamp: timestamp,
  };

  const model = { ...llm };
  updateRequestState(timestamp, 'database', 'pending', currentQuestion, [], model, meta_data_filters);

  try {
    const res = await post(`${getApiUrl()}/rag/database`, body, {
      contentType: 'application/json',
    });

    if (!res.ok) {
      const detail = await res.text().catch(() => null);
      throw new Error(detail || `HTTP ${res.status}`);
    }
    const data = await res.json();
    const mapped = mapItems(data, 'database', timestamp);

    updateRequestState(timestamp, 'database', 'done', currentQuestion, mapped, model);
    currentQuestion = '';
    resetTextAreaHeight();
  } catch (reason) {
    // eslint-disable-next-line no-console
    console.debug(uiTexts.rag.genericError + ` ${reason}`);
    updateRequestState(timestamp, 'database', 'error', currentQuestion, [], model);
  }
}

export async function fetchRAGResponse({
  timestamp,
  sources,
  currentQuestion,
  llm,
  updateRequestState,
  resetTextAreaHeight,
  uiTexts,
}) {
  const fileSources = sources.filter((s) => s.type === 'file' || s.type === 'clipboard');
  if (fileSources.length === 0) return;

  const formData = new FormData();
  const filesMeta = [];

  sources
    .filter((s) => s.type === 'file')
    .forEach((item) => {
      formData.append('files', item.content);
      filesMeta.push({ source: item.label });
    });

  formData.append('question', currentQuestion);
  formData.append('language_model', llm.name);
  if (llm.max_chunks_to_use != null) {
    formData.append('max_chunks_to_use', String(llm.max_chunks_to_use));
  }
  formData.append('request_timestamp', String(timestamp));

  const model = { ...llm };
  updateRequestState(timestamp, 'file', 'pending', currentQuestion, [], model, filesMeta);

  try {
    const res = await upload(`${getApiUrl()}/rag/file`, formData);

    if (!res.ok) {
      const detail = await res.text().catch(() => null);
      throw new Error(detail || `HTTP ${res.status}`);
    }

    const data = await res.json();
    const mapped = mapItems(data, 'file', timestamp);

    updateRequestState(timestamp, 'file', 'done', currentQuestion, mapped, model);
    currentQuestion = '';
    resetTextAreaHeight();
  } catch (reason) {
    // eslint-disable-next-line no-console
    console.debug(uiTexts.rag.genericError + ` ${reason}`);
    updateRequestState(timestamp, 'file', 'error', currentQuestion, [], model);
  }
}

export default {
  fetchRAGResponseDatabase,
  fetchRAGResponse,
};
